import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useTenant } from "../hooks/multiTenancy";
import { useQuery, useReactiveVar } from "@apollo/client";
import { useEffect, useState } from "react";
import { idAppCustomerDeviceVar } from "../pages/auth/login";
import { IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonGrid, IonIcon, IonImg, IonInfiniteScroll, IonInfiniteScrollContent, IonRefresher, IonRefresherContent, IonRow, RefresherEventDetail } from "@ionic/react";
import { IonInfiniteScrollCustomEvent } from "@ionic/core";
import { CommunicationItem, CommunicationType } from "../apollo-client/types";
import { communicationsQuery } from "../apollo-client/queries";
import { isPast } from "date-fns";
import { image } from "ionicons/icons";
import sanitizeHtml from "sanitize-html";

const PAGE_SIZE = 20;

type ModalProps = {
  selectedCommunicationType?: CommunicationType;
}

const NewsList: React.FC<ModalProps> = ({ selectedCommunicationType }) => {
  const {t} = useTranslation();
  const history = useHistory();
  const tenant = useTenant();
  const fidelitySalesPoints = tenant.settings?.mobileCommerceSalesPoints ?? [];
  const idAppCustomerDevice = useReactiveVar(idAppCustomerDeviceVar);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState(false);
  const [communications, setCommunications] = useState<CommunicationItem[]>([]);
  const queryVariables = {
    filter: encodeURIComponent(JSON.stringify({
      idAppCustomerDevice: idAppCustomerDevice !== "" ? idAppCustomerDevice : undefined,
      communicationType: selectedCommunicationType ? [selectedCommunicationType] : undefined,
      idFidelitySalesPoint: fidelitySalesPoints.map((p) => p.id),
    })),
    sorts: encodeURIComponent(JSON.stringify({ datetime: -1 })),
    start: 0,
    limit: PAGE_SIZE,
  };
  const { data, loading, fetchMore, refetch } = useQuery<{ communications: { records: CommunicationItem[] } }>(communicationsQuery, {
    variables: queryVariables,
  });
  useEffect(() => {
    if (data) {
      setCommunications(data.communications.records);
      setDisableInfiniteScroll(data.communications.records.length < PAGE_SIZE);
    }
  }, [data, setCommunications, setDisableInfiniteScroll, PAGE_SIZE]);
  const nextPage = async (e: IonInfiniteScrollCustomEvent<void>) => {
    const { data: newData } = await fetchMore({
      variables: {
        ...queryVariables,
        start: communications.length
      },
    });
    setCommunications([...communications, ...newData.communications.records]);
    if (newData.communications.records.length < PAGE_SIZE) {
      setDisableInfiniteScroll(true);
    }
    e.target.complete();
  };
  const refresh = async (e: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    e.detail.complete();
  };

  return (
    <>
      <IonRefresher slot="fixed" onIonRefresh={refresh}>
        <IonRefresherContent></IonRefresherContent>
      </IonRefresher>
      {!loading && communications.length === 0 && <div className="no-results-placeholder">{t("news.no_results")}</div>}
      {communications.length > 0 && (
        <IonGrid className="news-grid">
          <IonRow>
            {communications.map((c) => {
              const textMaxLength = 65;
              const sanitizedText = sanitizeHtml(c.text, { allowedTags: [], allowedAttributes: {} });
              let displayedText = sanitizedText;
              if (sanitizedText.length > textMaxLength) {
                displayedText = sanitizedText.slice(0, textMaxLength - 3).trimEnd() + "...";
              }
              if (sanitizedText.length < textMaxLength) {
                displayedText += "\xA0 ".repeat(textMaxLength - sanitizedText.length);
              }
              return (
                <IonCol key={c.id} size="12">
                  <IonCard onClick={() => history.push(`/app/others/news/${c.id}`)}>
                    <IonGrid>
                      <IonRow>
                        <IonCol size="3">
                          {c.image && (
                            <IonImg className="image-promo" src={c.image} style={{objectFit: "cover"}}></IonImg>
                          )}
                          {!c.image && (
                            <IonIcon className="image-promo" icon={image} style={{width: "100%"}} />
                          )}
                        </IonCol>
                        <IonCol size="9">
                          <IonCardHeader>
                            <IonCardTitle>{c.title}</IonCardTitle>
                          </IonCardHeader>
                          <IonCardContent>
                            <div>{displayedText}</div>
                            <div className="label">{c.appPromo && isPast(c.appPromo.validityEndDate * 1000) ? t("news.off_sale") : <div></div>}</div>
                          </IonCardContent>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </IonCard>
                </IonCol>
              );
            })}
          </IonRow>
        </IonGrid>
      )}
      <IonInfiniteScroll disabled={disableInfiniteScroll} threshold="200px" onIonInfinite={nextPage}>
        <IonInfiniteScrollContent loadingText={t("loading")} />
      </IonInfiniteScroll>
    </>
  );
};

export default NewsList;
